if (!customElements.get('custom-video-player')) {
  customElements.define('custom-video-player', class extends HTMLElement {
    static get observedAttributes() {
      return ['autoplay', 'loop', 'muted', 'show-play', 'show-mute', 'show-progress', 'show-fullscreen'];
    }

    constructor() {
      super();

      this.video = null;
      this.controls = null;
      this.progressBar = null;
      this.progressContainer = null;
      this.playButton = null;
      this.muteButton = null;
      this.fullscreenButton = null;
      this.playOverlay = null;
      this.playOverlayButton = null;
      this.volumeSlider = null;
      this.mutationObserver = null;
      this.initialized = false;

      this.hideControlsTimeout = null;
      this.hideControlsDelay = 3000;
      this.isInteracting = false;
      this.isSeeking = false;
      this.previousVolume = 1;

      this.boundHandlers = {
        handleVideoClick: this.handleVideoClick.bind(this),
        handlePlayPause: this.handlePlayPause.bind(this),
        handleMute: this.handleMute.bind(this),
        handleFullscreen: this.handleFullscreen.bind(this),
        handleTimeUpdate: this.handleTimeUpdate.bind(this),
        handleProgressClick: this.handleProgressClick.bind(this),
        handleProgressMouseDown: this.handleProgressMouseDown.bind(this),
        handleProgressMouseMove: this.handleProgressMouseMove.bind(this),
        handleProgressMouseUp: this.handleProgressMouseUp.bind(this),
        handleKeydown: this.handleKeydown.bind(this),
        handleMouseMove: this.handleMouseMove.bind(this),
        handleMouseLeave: this.handleMouseLeave.bind(this),
        handleVideoPlay: this.handleVideoPlay.bind(this),
        handleVideoPause: this.handleVideoPause.bind(this),
        handleVideoEnded: this.handleVideoEnded.bind(this),
        handleFullscreenChange: this.handleFullscreenChange.bind(this),
        handleVolumeChange: this.handleVolumeChange.bind(this),
        handleVolumeSliderInput: this.handleVolumeSliderInput.bind(this),
        handleTouchStart: this.handleTouchStart.bind(this),
        handlePosterClick: this.handlePosterClick.bind(this)
      };
    }

    connectedCallback() {
      this.initializePlayer();
    }

    initializePlayer() {
      if (this.initialized) return;

      this.video = this.querySelector('video');

      if (!this.video) {
        this.mutationObserver = new MutationObserver((mutations, obs) => {
          this.video = this.querySelector('video');
          if (this.video) {
            obs.disconnect();
            this.mutationObserver = null;
            this.setupPlayer();
          }
        });
        this.mutationObserver.observe(this, { childList: true, subtree: true });
        return;
      }

      this.setupPlayer();
    }

    setupPlayer() {
      if (this.initialized) return;
      this.initialized = true;

      this.controls = this.querySelector('.custom-video-player__controls');
      this.progressBar = this.querySelector('.custom-video-player__progress-bar');
      this.progressContainer = this.querySelector('.custom-video-player__progress-container');
      this.playButton = this.querySelector('.custom-video-player__button--play');
      this.muteButton = this.querySelector('.custom-video-player__button--mute');
      this.fullscreenButton = this.querySelector('.custom-video-player__button--fullscreen');
      this.playOverlay = this.querySelector('.custom-video-player__play-overlay');
      this.playOverlayButton = this.querySelector('.custom-video-player__play-overlay-button');
      this.volumeSlider = this.querySelector('.custom-video-player__volume-slider');
      this.poster = this.querySelector('.custom-video-player__poster');

      this.setupEventListeners();
      this.setupIntersectionObserver();
      this.updateMuteButtonState();
      this.updateVolumeSlider();
    }

    disconnectedCallback() {
      this.removeEventListeners();
      this.clearHideControlsTimeout();

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      this.initialized = false;
    }

    setupEventListeners() {
      this.video.addEventListener('click', this.boundHandlers.handleVideoClick);
      this.video.addEventListener('timeupdate', this.boundHandlers.handleTimeUpdate);
      this.video.addEventListener('play', this.boundHandlers.handleVideoPlay);
      this.video.addEventListener('pause', this.boundHandlers.handleVideoPause);
      this.video.addEventListener('ended', this.boundHandlers.handleVideoEnded);
      this.video.addEventListener('volumechange', this.boundHandlers.handleVolumeChange);

      if (this.playButton) {
        this.playButton.addEventListener('click', this.boundHandlers.handlePlayPause);
      }

      if (this.muteButton) {
        this.muteButton.addEventListener('click', this.boundHandlers.handleMute);
      }

      if (this.fullscreenButton) {
        this.fullscreenButton.addEventListener('click', this.boundHandlers.handleFullscreen);
      }

      if (this.playOverlay) {
        this.playOverlay.addEventListener('click', this.boundHandlers.handlePlayPause);
      }

      if (this.playOverlayButton) {
        this.playOverlayButton.addEventListener('click', this.boundHandlers.handlePlayPause);
      }

      if (this.poster) {
        this.poster.addEventListener('click', this.boundHandlers.handlePosterClick);
      }

      if (this.progressContainer) {
        this.progressContainer.addEventListener('click', this.boundHandlers.handleProgressClick);
        this.progressContainer.addEventListener('mousedown', this.boundHandlers.handleProgressMouseDown);
        this.progressContainer.addEventListener('touchstart', this.boundHandlers.handleProgressMouseDown, { passive: true });
      }

      if (this.volumeSlider) {
        this.volumeSlider.addEventListener('input', this.boundHandlers.handleVolumeSliderInput);
      }

      document.addEventListener('mousemove', this.boundHandlers.handleProgressMouseMove);
      document.addEventListener('mouseup', this.boundHandlers.handleProgressMouseUp);
      document.addEventListener('touchmove', this.boundHandlers.handleProgressMouseMove, { passive: true });
      document.addEventListener('touchend', this.boundHandlers.handleProgressMouseUp);

      this.addEventListener('keydown', this.boundHandlers.handleKeydown);
      this.addEventListener('mousemove', this.boundHandlers.handleMouseMove);
      this.addEventListener('mouseleave', this.boundHandlers.handleMouseLeave);
      this.addEventListener('touchstart', this.boundHandlers.handleTouchStart, { passive: true });

      document.addEventListener('fullscreenchange', this.boundHandlers.handleFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.boundHandlers.handleFullscreenChange);
    }

    removeEventListeners() {
      if (this.video) {
        this.video.removeEventListener('click', this.boundHandlers.handleVideoClick);
        this.video.removeEventListener('timeupdate', this.boundHandlers.handleTimeUpdate);
        this.video.removeEventListener('play', this.boundHandlers.handleVideoPlay);
        this.video.removeEventListener('pause', this.boundHandlers.handleVideoPause);
        this.video.removeEventListener('ended', this.boundHandlers.handleVideoEnded);
        this.video.removeEventListener('volumechange', this.boundHandlers.handleVolumeChange);
      }

      if (this.playButton) {
        this.playButton.removeEventListener('click', this.boundHandlers.handlePlayPause);
      }

      if (this.muteButton) {
        this.muteButton.removeEventListener('click', this.boundHandlers.handleMute);
      }

      if (this.fullscreenButton) {
        this.fullscreenButton.removeEventListener('click', this.boundHandlers.handleFullscreen);
      }

      if (this.playOverlay) {
        this.playOverlay.removeEventListener('click', this.boundHandlers.handlePlayPause);
      }

      if (this.playOverlayButton) {
        this.playOverlayButton.removeEventListener('click', this.boundHandlers.handlePlayPause);
      }

      if (this.poster) {
        this.poster.removeEventListener('click', this.boundHandlers.handlePosterClick);
      }

      if (this.progressContainer) {
        this.progressContainer.removeEventListener('click', this.boundHandlers.handleProgressClick);
        this.progressContainer.removeEventListener('mousedown', this.boundHandlers.handleProgressMouseDown);
        this.progressContainer.removeEventListener('touchstart', this.boundHandlers.handleProgressMouseDown);
      }

      if (this.volumeSlider) {
        this.volumeSlider.removeEventListener('input', this.boundHandlers.handleVolumeSliderInput);
      }

      document.removeEventListener('mousemove', this.boundHandlers.handleProgressMouseMove);
      document.removeEventListener('mouseup', this.boundHandlers.handleProgressMouseUp);
      document.removeEventListener('touchmove', this.boundHandlers.handleProgressMouseMove);
      document.removeEventListener('touchend', this.boundHandlers.handleProgressMouseUp);

      this.removeEventListener('keydown', this.boundHandlers.handleKeydown);
      this.removeEventListener('mousemove', this.boundHandlers.handleMouseMove);
      this.removeEventListener('mouseleave', this.boundHandlers.handleMouseLeave);
      this.removeEventListener('touchstart', this.boundHandlers.handleTouchStart);

      document.removeEventListener('fullscreenchange', this.boundHandlers.handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', this.boundHandlers.handleFullscreenChange);
    }

    setupIntersectionObserver() {
      if (!this.hasAttribute('autoplay')) return;

      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.play();
          } else {
            this.pause();
          }
        });
      }, { threshold: 0.5 });

      this.intersectionObserver.observe(this);
    }

    handleVideoClick(event) {
      event.preventDefault();
      this.togglePlay();
    }

    handlePosterClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.togglePlay();
    }

    handlePlayPause(event) {
      event.stopPropagation();
      this.togglePlay();
    }

    handleMute(event) {
      event.stopPropagation();
      this.toggleMute();
    }

    handleFullscreen(event) {
      event.stopPropagation();
      this.toggleFullscreen();
    }

    handleTimeUpdate() {
      this.updateProgressBar();
    }

    handleProgressClick(event) {
      this.seekToPosition(event);
    }

    handleProgressMouseDown(event) {
      this.isSeeking = true;
      this.seekToPosition(event);
    }

    handleProgressMouseMove(event) {
      if (!this.isSeeking) return;
      this.seekToPosition(event);
    }

    handleProgressMouseUp() {
      this.isSeeking = false;
    }

    handleVolumeSliderInput(event) {
      const value = parseFloat(event.target.value);
      this.video.volume = value;
      this.video.muted = value === 0;
      this.updateVolumeSlider();
    }

    handleKeydown(event) {
      switch (event.code) {
        case 'Space':
          event.preventDefault();
          this.togglePlay();
          break;
        case 'KeyM':
          event.preventDefault();
          this.toggleMute();
          break;
        case 'KeyF':
          event.preventDefault();
          this.toggleFullscreen();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.seek(this.video.currentTime - 5);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.seek(this.video.currentTime + 5);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setVolume(Math.min(1, this.video.volume + 0.1));
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.setVolume(Math.max(0, this.video.volume - 0.1));
          break;
      }
    }

    handleMouseMove() {
      this.showControls();
      this.scheduleHideControls();
    }

    handleMouseLeave() {
      if (!this.video.paused) {
        this.scheduleHideControls();
      }
    }

    handleTouchStart() {
      if (this.classList.contains('controls-hidden')) {
        this.showControls();
        this.scheduleHideControls();
      }
    }

    handleVideoPlay() {
      this.classList.add('is-playing');
      this.hidePoster();
      this.updatePlayButtonState();
      this.scheduleHideControls();
    }

    handleVideoPause() {
      this.classList.remove('is-playing');
      this.updatePlayButtonState();
      this.showControls();
      this.clearHideControlsTimeout();
    }

    handleVideoEnded() {
      this.classList.remove('is-playing');
      this.updatePlayButtonState();
      this.showControls();
      this.clearHideControlsTimeout();
    }

    handleFullscreenChange() {
      this.updateFullscreenButtonState();
    }

    handleVolumeChange() {
      this.updateMuteButtonState();
      this.updateVolumeSlider();
    }

    play() {
      if (this.video) {
        const playPromise = this.video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            this.showPoster();
          });
        }
      }
    }

    showPoster() {
      this.classList.remove('is-playing');
      const poster = this.querySelector('.custom-video-player__poster');
      if (poster) {
        poster.style.opacity = '1';
        poster.style.pointerEvents = 'auto';
      }
    }

    hidePoster() {
      const poster = this.querySelector('.custom-video-player__poster');
      if (poster) {
        poster.style.opacity = '0';
        poster.style.pointerEvents = 'none';
      }
    }

    pause() {
      if (this.video) {
        this.video.pause();
      }
    }

    togglePlay() {
      if (this.video.paused) {
        this.play();
      } else {
        this.pause();
      }
    }

    mute() {
      if (this.video) {
        this.video.muted = true;
      }
    }

    unmute() {
      if (this.video) {
        this.video.muted = false;
        if (this.video.volume === 0) {
          this.video.volume = this.previousVolume || 1;
        }
      }
    }

    toggleMute() {
      if (this.video) {
        if (this.video.muted || this.video.volume === 0) {
          this.video.muted = false;
          this.video.volume = this.previousVolume || 1;
        } else {
          this.previousVolume = this.video.volume;
          this.video.muted = true;
        }
      }
    }

    setVolume(value) {
      if (this.video) {
        this.video.volume = value;
        this.video.muted = value === 0;
        if (value > 0) {
          this.previousVolume = value;
        }
        this.updateVolumeSlider();
      }
    }

    enterFullscreen() {
      if (this.requestFullscreen) {
        this.requestFullscreen();
      } else if (this.webkitRequestFullscreen) {
        this.webkitRequestFullscreen();
      } else if (this.video.webkitEnterFullscreen) {
        this.video.webkitEnterFullscreen();
      }
    }

    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }

    toggleFullscreen() {
      if (this.isFullscreen()) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    }

    isFullscreen() {
      return document.fullscreenElement === this || document.webkitFullscreenElement === this;
    }

    seek(time) {
      if (this.video) {
        this.video.currentTime = Math.max(0, Math.min(time, this.video.duration));
      }
    }

    seekToPosition(event) {
      if (!this.progressContainer || !this.video) return;

      const rect = this.progressContainer.getBoundingClientRect();
      let clientX;

      if (event.touches && event.touches.length > 0) {
        clientX = event.touches[0].clientX;
      } else {
        clientX = event.clientX;
      }

      const position = (clientX - rect.left) / rect.width;
      const clampedPosition = Math.max(0, Math.min(1, position));
      this.video.currentTime = clampedPosition * this.video.duration;
    }

    updateProgressBar() {
      if (!this.progressBar || !this.video) return;

      const progress = (this.video.currentTime / this.video.duration) * 100;
      this.progressBar.style.width = `${progress}%`;
    }

    updatePlayButtonState() {
      if (!this.playButton) return;

      const playIcon = this.playButton.querySelector('.icon-play, .icon-play-control');
      const pauseIcon = this.playButton.querySelector('.icon-pause, .icon-pause-control');

      if (this.video.paused) {
        this.playButton.setAttribute('aria-label', 'Play video');
        if (playIcon) playIcon.style.display = '';
        if (pauseIcon) pauseIcon.style.display = 'none';
      } else {
        this.playButton.setAttribute('aria-label', 'Pause video');
        if (playIcon) playIcon.style.display = 'none';
        if (pauseIcon) pauseIcon.style.display = '';
      }
    }

    updateMuteButtonState() {
      if (!this.muteButton || !this.video) return;

      const muteIcon = this.muteButton.querySelector('.icon-video-mute');
      const unmuteIcon = this.muteButton.querySelector('.icon-video-unmute');
      const isMuted = this.video.muted || this.video.volume === 0;

      if (isMuted) {
        this.muteButton.setAttribute('aria-label', 'Unmute video');
        if (muteIcon) muteIcon.style.display = 'none';
        if (unmuteIcon) unmuteIcon.style.display = '';
      } else {
        this.muteButton.setAttribute('aria-label', 'Mute video');
        if (muteIcon) muteIcon.style.display = '';
        if (unmuteIcon) unmuteIcon.style.display = 'none';
      }
    }

    updateVolumeSlider() {
      if (!this.volumeSlider || !this.video) return;

      const volume = this.video.muted ? 0 : this.video.volume;
      this.volumeSlider.value = volume;
      this.volumeSlider.style.setProperty('--volume-percent', `${volume * 100}%`);
    }

    updateFullscreenButtonState() {
      if (!this.fullscreenButton) return;

      const fullscreenIcon = this.fullscreenButton.querySelector('.icon-video-fullscreen');
      const exitIcon = this.fullscreenButton.querySelector('.icon-video-fullscreen-exit');

      if (this.isFullscreen()) {
        this.fullscreenButton.setAttribute('aria-label', 'Exit fullscreen');
        if (fullscreenIcon) fullscreenIcon.style.display = 'none';
        if (exitIcon) exitIcon.style.display = '';
      } else {
        this.fullscreenButton.setAttribute('aria-label', 'Enter fullscreen');
        if (fullscreenIcon) fullscreenIcon.style.display = '';
        if (exitIcon) exitIcon.style.display = 'none';
      }
    }

    showControls() {
      this.classList.remove('controls-hidden');
    }

    hideControls() {
      if (!this.video.paused) {
        this.classList.add('controls-hidden');
      }
    }

    scheduleHideControls() {
      this.clearHideControlsTimeout();
      this.hideControlsTimeout = setTimeout(() => {
        this.hideControls();
      }, this.hideControlsDelay);
    }

    clearHideControlsTimeout() {
      if (this.hideControlsTimeout) {
        clearTimeout(this.hideControlsTimeout);
        this.hideControlsTimeout = null;
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this.video) return;

      switch (name) {
        case 'autoplay':
          this.video.autoplay = newValue !== null;
          break;
        case 'loop':
          this.video.loop = newValue !== null;
          break;
        case 'muted':
          this.video.muted = newValue !== null;
          break;
      }
    }
  });
}
